import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import DialogueBody from 'src/components/GenericDialogueBody/GenericDialogueBody';
import DialogueFooter from 'src/components/GenericDialogueFooter/GenericDialogueFooter';
import RowValidationErrorCard from 'src/pages/Property/HomeOwnerImportDialogue/Components/RowValidationCard/RowValidationCard';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  REQUIRED_CATALOGUE_COLUMNS,
  useCatalogueImport,
} from 'src/pages/Catalog/Components/CatalogueImportDialogue/CustomHooks/useCatalogueImport';
import {
  CatalogItem,
  CatalogueImportModel,
} from 'src/pages/Catalog/Components/CatalogueImportDialogue/CustomHooks/types';
import './CatalogueImportDialogue.scss';
import PreviewTable, {
  ColumnConfig,
} from 'src/pages/Property/HomeOwnerImportDialogue/Components/PreviewTable/PreviewTable';
import CsvTemplateDownloadCard from 'src/components/CsvTemplateDownloadCard/CsvTemplateDownloadCard';

const catalogueColumns: ColumnConfig<CatalogueImportModel>[] = [
  { key: 'Name', header: 'Name' },
  { key: 'Type', header: 'Type' },
  { key: 'Warranty', header: 'Warranty' },
  { key: 'Make', header: 'Make' },
  { key: 'Model', header: 'Model' },
  {
    key: 'Properties',
    header: 'Properties',
    render: (v: { Name: string }[]) => v.map((p) => p.Name).join(', ')
  },
];

interface CatalogueImportDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void
}

const CatalogueImportDialogue = ({
  isOpen,
  onClose,
  onImportComplete
}: CatalogueImportDialogueProps) => {
  const user = useSelector(selectAuthenticatedUser);

  const {
    file,
    validationStatus,
    columnValidation,
    parsedRecords,
    importStatus,
    importResult,
    apiError,
    rowValidationErrors,
    processFile,
    runImport,
    reset,
  } = useCatalogueImport();

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept: '.csv',
    multiple: false,
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        processFile(acceptedFiles[0]);
      }
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleImportComplete = useCallback(() => {
    reset();
    onImportComplete();
    onClose();
  }, [reset, onImportComplete, onClose]);

  const handleRunImport = useCallback(() => {
    runImport(user.id);
  }, [runImport, user.id]);

  const isImporting: boolean = importStatus === 'importing';
  const isSuccess: boolean = importStatus === 'success';
  const isImportError: boolean = importStatus === 'error';
  const isValidCsv: boolean = validationStatus === 'valid';
  const isImportEnabled: boolean = isValidCsv && !isImporting;

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Catalog Items"
      description="Upload a CSV to bulk-add items to your catalog"
      maxWidth="1100px"
      footer={
        <DialogueFooter
          isSuccess={isSuccess}
          isImporting={isImporting}
          file={file}
          isValidCsv={isValidCsv}
          isImportError={isImportError}
          isImportEnabled={isImportEnabled}
          importButtonLabel="Import Items"
          reset={reset}
          handleImportComplete={handleImportComplete}
          handleClose={handleClose}
          runImport={handleRunImport}
        />
      }
    >
      <div className="cid_body">
        {!file && !isSuccess && (
          <CsvTemplateDownloadCard
            filename="catalogue-import-template.csv"
            columns={REQUIRED_CATALOGUE_COLUMNS}
          />
        )}
        <DialogueBody
          isSuccess={isSuccess}
          isImporting={isImporting}
          file={file}
          importResult={importResult}
          recordCount={parsedRecords.length}
          validationStatus={validationStatus}
          columnValidation={columnValidation}
          hasRowErrors={rowValidationErrors.length > 0}
          isImportError={isImportError}
          apiError={apiError}
          entityName="catalog item"
          requiredColumns={REQUIRED_CATALOGUE_COLUMNS}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
          reset={reset}
        >
          {rowValidationErrors.length > 0 && (
            <RowValidationErrorCard errors={rowValidationErrors} />
          )}
          {validationStatus === 'valid' && (
            <PreviewTable records={parsedRecords} columns={catalogueColumns} />
          )}
        </DialogueBody>
      </div>
    </GenericDialogue>
  );
};

export default CatalogueImportDialogue;
